import React, { useState } from 'react'
import { Home } from 'react-feather'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useHistory } from 'react-router-dom'
import _ from 'lodash'
import { CardBody, Card, CardTitle } from 'reactstrap'
import { useFormik } from 'formik'
import SweetAlert from 'react-bootstrap-sweetalert'
import styled from 'styled-components'
import { toast } from 'react-toastify'

import ImagesForm from './EditInformations/ImagesForm'

import api from '~/services/api'
import uploadFile from '~/api/uploadFile'
import Breadcrumb from '~/components/Breadcrumb'
import { Row, Col, Show } from '~/components/commons'
import { queries } from '~/constants/mediaQuery'
import { Container } from './styles'
import { useSelector } from 'react-redux'
import FilterPanel from './commons/FilterPanel'
import ScheduleSubmitForm from './commons/ScheduleSubmitForm'
import PreviewImages from './Preview/PreviewImages'

function deepPickBy(obj) {
  const pick = object =>
    _.pickBy(object, item => {
      return !!item && !_.isEmpty(item)
    })

  const res = Object.entries(obj).reduce((acc, [key, value]) => {
    if (_.isObject(value) && !_.isArray(value)) {
      const parsed = pick(value)
      if (Object.keys(parsed)?.length) {
        acc[key] = parsed
      }
    } else if (
      (_.isArray(value) && !!value.length) ||
      (!_.isArray(value) && !!value)
    ) {
      acc[key] = value
    }

    return acc
  }, {})

  _.pickBy(obj, item => {
    return !!item && !_.isEmpty(item)
  })

  return res
}

CardContainer.Body = styled(CardBody)`
  padding: 2.5rem;
  overflow-x: auto;
  overflow: visible;

  @media ${queries.less.xl} {
    padding: 1.5rem;
  }
`
function CardContainer({ children, title }) {
  return (
    <Card>
      <CardContainer.Body>
        <Show condition={!!title}>
          <CardTitle>
            <FormattedMessage id={title} />
          </CardTitle>
        </Show>
        {children}
      </CardContainer.Body>
    </Card>
  )
}

function Alert({ type = 'info', visible = false, onClose, body, ...props }) {
  return (
    <SweetAlert
      info={type === 'info'}
      success={type === 'success'}
      error={type === 'error'}
      show={visible}
      onConfirm={onClose}
      {...props}
    >
      <p className="sweet-alert-text">{body}</p>
    </SweetAlert>
  )
}

function EditImages() {
  const intl = useIntl()
  const history = useHistory()
  const projectId = useSelector(state => state.user?.selectedProject?.id)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [valid, setValid] = useState(false)
  const [alertsVisible, setAlertsVisible] = useState({
    error: false,
    updated: false,
    scheduled: false,
  })
  const [removeLabels, setRemoveLabels] = useState(false)
  const [images, setImages] = useState({})
  const [locationNames, setLocationNames] = useState([])
  const [opened, setOpened] = useState(false)
  const [scheduleDate, setScheduleDate] = useState(null)

  const informationsFormik = useFormik({
    initialValues: {
      scheduleEdition: '',
      images: {
        logo: null,
        cover: null,
        interior: null,
        exterior: null,
        additional: null,
      },
    },
  })

  async function handleSubmit() {
    setError('')
    setSubmitting(true)

    const { scheduleEdition } = deepPickBy(informationsFormik.values)

    const params = {
      validateOnly: 'false',
    }

    try {
      await api.patch(
        `/projects/${projectId}/locations/images`,
        { images, locations: locationNames, scheduleEdition },
        {
          params,
        }
      )

      setAlert(scheduleEdition ? 'scheduled' : 'updated', true)
      setSubmitting(false)
      return true
    } catch (err) {
      setAlert('error', true)
      setError(
        err?.response?.data?.error ||
          err?.message ||
          'Your locations NOT updated!'
      )
      setSubmitting(false)
      return false
    }
  }

  async function handleValidateSubmit() {
    const handleError = msg => {
      setValid(false)
      setError(msg)
      setAlert('error', true)
      return false
    }

    setError('')

    const values = deepPickBy(informationsFormik.values)

    setScheduleDate(values.scheduleEdition)

    if (removeLabels && !values?.labels?.length) values.labels = []

    if (_.isEmpty(values)) {
      return handleError('You must edit something!')
    }

    if (!values?.locationNames?.length) {
      return informationsFormik.setErrors({
        locationNames: 'You must select some location to update',
      })
    }

    const locations = [...values.locationNames]
    setLocationNames(locations)
    delete values.locationNames

    const uploadedImages = {}

    if (!values?.images) return toast.warn('There is no image to update!')

    const arrayOfPicturesToUpload = Object.entries(
      values.images
    ).map(([key, values]) =>
      values?.map(image => ({ origin: key, file: image }))
    )

    const promisesPicUpload = _.flatten(arrayOfPicturesToUpload).map(
      pic =>
        new Promise((resolve, reject) => {
          uploadFile(pic.file)
            .then(({ data: picture }) => {
              if (!uploadedImages[pic.origin]) uploadedImages[pic.origin] = []
              uploadedImages[pic.origin].push(picture)
              delete values[pic.origin]
              resolve(picture)
            })
            .catch(error => {
              toast.error(
                `We were unable to upload your ${pic.origin} image. try again.`
              )
              reject(error)
            })
        })
    )

    await Promise.all(promisesPicUpload)
    setImages(uploadedImages)

    setOpened(true)

    try {
      await api.patch(
        `/projects/${projectId}/locations/images`,
        { locations, images: uploadedImages },
        {
          params: {
            validateOnly: 'true',
          },
        }
      )

      setValid(true)
      toast.success(
        intl.formatMessage({ id: 'The information to be updated is valid!' })
      )
      return true
    } catch (err) {
      toast.success(
        intl.formatMessage({ id: 'The information to be updated is invalid!' })
      )
      return handleError(
        err?.response?.data?.error ||
          err?.message ||
          'Your locations were NOT updated!'
      )
    }
  }

  function gotoHome() {
    history.push('/')
  }

  function setAlert(name, value) {
    setAlertsVisible(old => ({
      ...old,
      [name]: value,
    }))
  }

  const toggle = () => {
    if (submitting) return
    setOpened(prev => !prev)
  }

  return (
    <>
      <Container>
        <Breadcrumb
          paths={[
            {
              render: () => (
                <Link className="d-flex align-items-center" to="/">
                  <Home size={20} />
                </Link>
              ),
            },
            {
              render: () => (
                <strong className="text-primary font-medium-3">
                  <FormattedMessage id="Informations" />
                </strong>
              ),
            },
            {
              render: () => (
                <span className="font-medium-3">
                  <FormattedMessage id="Images" />
                </span>
              ),
            },
          ]}
        />

        <Card>
          <Row>
            <Col md={6}>
              <CardContainer>
                <ImagesForm
                  formik={informationsFormik}
                  onChangeRemoveLabels={setRemoveLabels}
                />
              </CardContainer>
            </Col>
            <Col md={6}>
              <CardContainer>
                <FilterPanel formik={informationsFormik} />
                <ScheduleSubmitForm
                  formik={informationsFormik}
                  onSubmit={handleValidateSubmit}
                  loading={submitting}
                />
              </CardContainer>
            </Col>
          </Row>
        </Card>
        <PreviewImages
          toggleModal={toggle}
          isOpen={opened}
          images={images}
          onSubmit={() => handleSubmit()}
          isSubmiting={submitting}
          isValid={valid}
          scheduleEdition={scheduleDate}
        />
      </Container>
      <Alert
        type="error"
        visible={alertsVisible.error}
        onClose={() => setAlert('error', false)}
        onCancel={() => setAlert('error', false)}
        confirmBtnText="Try again"
        body={error}
        title="Error"
        closeOnClickOutside
      />
      <Alert
        type="success"
        visible={alertsVisible.scheduled}
        onClose={gotoHome}
        confirmBtnText="Go to dashboard"
        body="Your edits have been scheduled."
        title="Successful Scheduling"
      />
      <Alert
        type="success"
        visible={alertsVisible.updated}
        onClose={gotoHome}
        confirmBtnText="Go to dashboard"
        body="Your locations are updated!"
        title="Success"
      />
    </>
  )
}

export default EditImages
